.container {
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  overflow: hidden;
}

// Opt-in horizontal scroll (TableContainer scrollable). The container
// scrolls sideways. The row's total floor is set by a single
// `min-width` (the sum of the column bases, passed inline as
// `--row-min-width`) — NOT `max-content`, which would size every row to
// its own content and make columns drift. With one shared floor every
// row is the same width, so the cells line up.
.is-scrollable {
  // `.container`'s `overflow: hidden` (which keeps the end rows inside the 12px
  // radius) clips VERTICALLY too, so this card must never be sized below its
  // rows: as a flex item in a column it would otherwise shrink to the free
  // space and swallow every row past the fold, leaving the scrolling ancestor
  // with `scrollHeight === clientHeight` and no scrollbar to offer (XT-156).
  // Scoped to `scrollable` on purpose — a plain `.container` may legitimately be
  // a shrink-to-fit flex item wrapping its own inner scroller (see
  // `products-table.module.scss`), and must keep `flex-shrink: 1`.
  flex-shrink: 0;
  overflow-x: auto;

  .row,
  .footer {
    min-width: var(--row-min-width, 100%);
  }
}

@media (max-width: 768px) {
  .container {
    overflow-x: auto;
  }

  .row {
    min-width: 600px;
  }

  .footer {
    min-width: 600px;
  }
}

.row {
  align-items: center;
  border-bottom: 1px solid var(--color-border);
  display: flex;
  transition: background-color 0.15s;

  &.isHeader {
    background: transparent;
    color: var(--color-foreground);
    font-size: 14px;
    font-weight: 500;
    height: 56px;
  }

  &:not(.isHeader) {
    background: var(--color-background);
    font-size: 14px;
    height: 52px;
  }

  &:not(.isHeader):hover {
    background: var(--color-surface);
  }

  // Opt-in density (see `compact` on TableRow).
  &.isCompact {
    font-size: 13px;
  }

  &.isCompact.isHeader {
    height: 38px;
  }

  &.isCompact:not(.isHeader) {
    height: 44px;
  }

  // Wrapping cell content (e.g. role pills) would otherwise overflow the
  // fixed 52px row and paint over the header. Grow the row instead.
  &.isAutoHeight:not(.isHeader) {
    align-items: center;
    height: auto;
    min-height: 52px;
  }
}

.cell {
  flex: 1;
  // `min-width: 0` lets a flex item shrink below its content's intrinsic
  // size so `overflow: hidden` truncates instead of the long content
  // inflating the cell (which would push the whole row wider than its
  // siblings and break column alignment). A fixed-`width` cell overrides
  // this inline.
  min-width: 0;
  overflow: hidden;
  padding: 0 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// Header cells hold a short label plus a sort button (label + arrows), never
// long free text. The generic `.cell` ellipsis would paint a stray `…` after
// the button whenever the label + arrows are a hair wider than the column
// (e.g. a narrow "Viewed" column). Headers don't need truncation — the table
// scrolls horizontally — so clear the ellipsis machinery for header cells.
.row.isHeader .cell {
  overflow: visible;
  text-overflow: clip;
}

.cell-align-center {
  text-align: center;
}

.cell-align-right {
  text-align: right;
}

.footer {
  align-items: center;
  background: var(--color-surface);
  border-top: 1px solid var(--color-border);
  display: flex;
  height: 44px;
  justify-content: space-between;
  padding: 0 16px;
}
